import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {NIVEL_TONE} from '@/lib/bateria'

/** Antes de leer algo es «—», nunca una barra vacía que sugiera 0 de 0. */
export function PaginasCelda({paquete}: {paquete: BateriaPaquete}) {
  if (paquete.paginas_total === 0) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <div className="space-y-1">
      <span className="font-mono text-[11px] text-muted-foreground">
        {paquete.paginas_hechas}/{paquete.paginas_total}
      </span>
      <div className="h-1 w-16 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full bg-primary"
          style={{width: `${Math.round((paquete.paginas_hechas / paquete.paginas_total) * 100)}%`}}
        />
      </div>
    </div>
  )
}

/** Lo que falta por decidir, no las banderas crudas del motor: esas no bajan al confirmar. */
export function PendientesCelda({paquete, onIr}: {paquete: BateriaPaquete; onIr: () => void}) {
  const {t} = useTranslation()
  if (paquete.pendientes_de_decision === 0) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <button
      type="button"
      data-testid={`pendientes-${paquete.id}`}
      onClick={(e) => {
        e.stopPropagation()
        onIr()
      }}>
      <Tag tone="amber">{t('admin.bateria.detail.pendientesCount', {count: paquete.pendientes_de_decision})}</Tag>
    </button>
  )
}

// Mismo orden fijo que `puntajes.cuestionarios` del motor (`scoring.py`); es de presentación.
const CUESTIONARIOS_RIESGO = ['intralaboral', 'extralaboral', 'estres'] as const

/** Nivel por cuestionario y no por dominio: 19 dominios no caben en una celda. Un cuestionario sin
 * nivel (invalidez del motor) no aparece: un chip sin color sería peor. */
export function RiesgoCelda({paquete, onVerResultado}: {paquete: BateriaPaquete; onVerResultado: () => void}) {
  const {t} = useTranslation()
  if (!paquete.niveles_cuestionario) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  const niveles = paquete.niveles_cuestionario
  return (
    <button
      type="button"
      data-testid={`riesgo-${paquete.id}`}
      className="flex flex-wrap items-center gap-1"
      onClick={(e) => {
        e.stopPropagation()
        onVerResultado()
      }}>
      {CUESTIONARIOS_RIESGO.filter((c) => niveles[c]).map((c) => (
        // U17: el chip abrevia y su tono es de magnitud, así que nombre y nivel van en `sr-only`;
        // `aria-label` no sirve en un `<span>` sin `role`.
        <Tag key={c} tone={NIVEL_TONE[niveles[c]] ?? 'neutral'}>
          <span aria-hidden="true">{t(`admin.bateria.cuestionarioCorto.${c}`)}</span>
          <span className="sr-only">
            {t(`admin.bateria.cuestionario.${c}`)} · {t(`admin.bateria.nivel.${niveles[c]}`)}
          </span>
        </Tag>
      ))}
    </button>
  )
}
